import React, { useContext, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { Text } from '@bingads-webui-react/fabric-control';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { PropertyRow } from '@bingads-webui-campaign-react/ui-generator';
import { getDay, getMin, getTime, minuteToSaveMap } from '@bingads-webui-campaign-react/wizard-shared-components';
import { getDateSettingDetails } from '@bingads-webui-campaign/date-setting';

export const getDayTimeDetails = (i18n, dayTime, is12) => {
  const startHour = dayTime.StartHour;
  const endHour = dayTime.EndHour;
  const startMin = getMin(minuteToSaveMap[dayTime.StartMinute]);
  const endMin = getMin(minuteToSaveMap[dayTime.EndMinute]);
  const dateTimePart = `${i18n.getString(getDay(dayTime.Day))} ${i18n.getString(_TL_('from {{from}} to {{to}}'), {
    from: i18n.getString(getTime(startHour, is12), { min: startMin }),
    to: i18n.getString(getTime(endHour, is12), { min: endMin }),
  })}`;

  return dateTimePart;
};

const getAdScheduleDetails = (i18n, adSchedules, timeDisplayType, useSearcherTimeZone) => {
  let details = '';

  const is12 = timeDisplayType === '12-hour';
  const len = adSchedules.length;

  if (len === 0) {
    const defaultSchedule = {
      Id: '',
      Day: 'Alldays',
      StartHour: 0,
      StartMinute: 'ZERO',
      EndHour: 24,
      EndMinute: 'ZERO',
      BidAdjustmentType: 1,
      BidAdjustmentAmount: 0,
      CashbackPercent: null,
    };

    details += getDayTimeDetails(i18n, defaultSchedule, is12);
  } else {
    _.forEach(adSchedules, (schedule, index) => {
      details += getDayTimeDetails(i18n, schedule, is12);
      if (details && index < len - 1) {
        details += ', ';
      }
    });
  }

  if (useSearcherTimeZone) {
    details += ` ${i18n.getString(_TL_('In Ad viewer\'s time zone'))}`;
  } else {
    details += ` ${i18n.getString(_TL_('In Your account\'s time zone'))}`;
  }

  return details;
};

export const AdSchedule = ({
  startDate, endDate, adSchedules, useSearcherTimeZone, timeDisplayType, includeStartEnd,
}) => {
  const i18n = useContext(I18nContext);

  const dateSettings = useMemo(() => (includeStartEnd ? getDateSettingDetails(i18n, startDate, endDate) : ''), [endDate, i18n, includeStartEnd, startDate]);
  const adScheduleSettings = useMemo(
    () => (adSchedules ?
      getAdScheduleDetails(i18n, adSchedules, timeDisplayType, useSearcherTimeZone) : '')
    , [adSchedules, i18n, timeDisplayType, useSearcherTimeZone]
  );

  return (
    <PropertyRow propertyLabel={{ label: _TL_('Schedule') }}>
      <Text>{includeStartEnd ? `${dateSettings}${adScheduleSettings && `: ${adScheduleSettings}`}` : adScheduleSettings}</Text>
    </PropertyRow>
  );
};

AdSchedule.propTypes = {
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string,
  adSchedules: PropTypes.arrayOf(PropTypes.shape({})),
  useSearcherTimeZone: PropTypes.bool,
  timeDisplayType: PropTypes.string,
  includeStartEnd: PropTypes.bool,
};

AdSchedule.defaultProps = {
  endDate: null,
  adSchedules: null,
  useSearcherTimeZone: false,
  timeDisplayType: '12-hour',
  includeStartEnd: true,
};
